import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fadeUp } from "./content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Face to Face Locum Clinics",
  "Remote Clinical Triage",
  "Remote Clinical Workflow Admin",
  "Not sure yet / General enquiry",
];

const EMPTY = { name: "", surgery: "", email: "", phone: "", service: "", message: "", company_website: "" };

const fieldClass =
  "bg-bone border-sage/70 text-forest placeholder:text-ink-soft/60 rounded-[6px] h-12 focus-visible:ring-forest focus-visible:border-forest";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.surgery || !form.email || !form.message) {
      toast.error("Please fill in your name, surgery, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Thank you — your enquiry has been sent.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-36 bg-bone" data-testid="contact-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="lg:col-span-5"
          >
            <span className="overline text-ink-soft">Enquire</span>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-forest">
              Tell me about your cover gap.
            </h2>
            <p className="mt-6 max-w-md text-lg text-ink-soft leading-relaxed">
              Share a few details about your surgery and the support you need. I'll come back to you
              quickly with availability and how we can help.
            </p>
            <div className="mt-10 space-y-4">
              {["Response within one working day", "No obligation, no hard sell", "On-site or fully remote"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-forest">
                  <CheckCircle2 className="h-5 w-5 text-forest-accent" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            custom={1}
            className="lg:col-span-7 rounded-[10px] border border-surface-alt bg-surface p-8 md:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.04)]"
          >
            {done ? (
              <div className="flex flex-col items-center text-center py-16" data-testid="contact-success">
                <CheckCircle2 className="h-14 w-14 text-forest-accent" />
                <h3 className="mt-6 font-display text-3xl text-forest">Enquiry received</h3>
                <p className="mt-3 max-w-sm text-ink-soft">
                  Thank you for getting in touch with Wellknowe Ltd. I'll be in contact shortly.
                </p>
                <button
                  onClick={() => setDone(false)}
                  className="mt-8 inline-flex items-center gap-2 rounded-full border border-sage px-6 py-3 text-forest hover:bg-surface-alt transition-colors duration-200"
                  data-testid="contact-reset"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                {/* Honeypot field — hidden from users, catches bots */}
                <input
                  type="text"
                  name="company_website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.company_website}
                  onChange={update("company_website")}
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                  aria-hidden="true"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="overline text-ink-soft mb-2 block">Your name*</label>
                    <Input value={form.name} onChange={update("name")} placeholder="Jane Smith" className={fieldClass} data-testid="contact-name" />
                  </div>
                  <div>
                    <label className="overline text-ink-soft mb-2 block">Surgery / practice*</label>
                    <Input value={form.surgery} onChange={update("surgery")} placeholder="Riverside Medical Practice" className={fieldClass} data-testid="contact-surgery" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="overline text-ink-soft mb-2 block">Email*</label>
                    <Input type="email" value={form.email} onChange={update("email")} placeholder="jane@surgery.nhs.uk" className={fieldClass} data-testid="contact-email" />
                  </div>
                  <div>
                    <label className="overline text-ink-soft mb-2 block">Phone</label>
                    <Input value={form.phone} onChange={update("phone")} placeholder="01234 567890" className={fieldClass} data-testid="contact-phone" />
                  </div>
                </div>

                <div>
                  <label className="overline text-ink-soft mb-2 block">Service of interest</label>
                  <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                    <SelectTrigger className={fieldClass} data-testid="contact-service">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="overline text-ink-soft mb-2 block">How can I help?*</label>
                  <Textarea value={form.message} onChange={update("message")} placeholder="Tell me about the cover you need, dates, and clinical system you use…" rows={5} className={`${fieldClass} h-auto py-3`} data-testid="contact-message" />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-forest px-8 py-4 text-base font-medium text-bone hover:bg-forest-accent transition-colors duration-200 disabled:opacity-60 group"
                  data-testid="contact-submit"
                >
                  {loading ? "Sending…" : "Send enquiry"}
                  {!loading && <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
